/* Closing a contract, in whole or in part.
 *
 * Taking half off is not a different act from taking all of it off, so it is one
 * calculation with a portion in it. What scales is the margin posted and the
 * notional at entry — both are quantities of contracts, and closing a third of
 * the position leaves two thirds of each. What does not scale is the entry
 * price: it is an average of what was paid, and selling some of it does not
 * change what the rest cost (the same rule §5.1 applies to shares).
 *
 * The liquidation price is left alone. With isolated margin, reducing size and
 * margin together barely moves it — but "barely" is not "exactly", and this is
 * a figure the exchange computes from its own maintenance rules. Recomputing it
 * here would be inventing a number that decides when you get liquidated. */

import type { Perp, PerpClose } from '../types';

export interface CloseOutcome {
  /** The contract as it stands afterwards, or null when nothing is left of it. */
  next: Perp | null;
  /** Margin released plus the result, which is what lands back in the wallet. */
  toWallet: number;
  /** True when this took the last of it. */
  closedOut: boolean;
}

const EPSILON = 1e-9;

export function closePortion(
  perp: Perp,
  portion: number,
  resultUsdt: number,
  at = new Date().toISOString(),
  /** True when the exchange had already settled it and the book's balance was
      read afterwards, so recording it moved no money here. */
  settled = false,
): CloseOutcome {
  const share = Math.min(1, Math.max(0, portion));
  const releasedMargin = perp.marginUsdt * share;
  const toWallet = releasedMargin + resultUsdt;

  /* Kept as an event, not only as a running total. A sum cannot say which day
     it happened on, and "what did this book realise in August" is a question
     about days. */
  const event: PerpClose = {
    at, resultUsdt, portion: share, releasedUsdt: releasedMargin,
    ...(settled ? { settled: true } : {}),
  };
  const closes = [...(perp.closes ?? []), event];

  if (share >= 1 - EPSILON) {
    /* The margin and the notional are kept, not zeroed.
     *
     * Zeroing them was meant to stop a closed contract counting towards
     * exposure — but nothing was ever counting it: the totals filter on
     * `closedAt`, not on the figures. All the zeros did was destroy the record
     * of how big the trade was, which is most of what there is to look at
     * afterwards. A contract that closed at +18.13 on 170 of margin and one
     * that closed at +18.13 on 17,000 are not the same trade, and the book had
     * no way to tell them apart the moment the position shut.
     *
     * Contracts closed before this change carry the zeros and cannot be
     * recovered; their detail screen says the figure is not there rather than
     * printing a zero as though it were one. */
    return {
      next: {
        ...perp,
        closes,
        closedAt: at,
        closedResultUsdt: (perp.realizedUsdt ?? 0) + resultUsdt,
      },
      toWallet,
      closedOut: true,
    };
  }

  return {
    next: {
      ...perp,
      closes,
      marginUsdt: perp.marginUsdt - releasedMargin,
      sizeUsdt: perp.sizeUsdt * (1 - share),
      // What has already been taken off the table, carried on the contract.
      realizedUsdt: (perp.realizedUsdt ?? 0) + resultUsdt,
    },
    toWallet,
    closedOut: false,
  };
}

/* ---------- taking a close back ---------- */

export interface UndoOutcome {
  /** The contract as it stood before that close. */
  next: Perp;
  /** What has to come back out of the wallet: the margin it released and the
      result it credited. Zero when the close moved no money because the
      exchange had already settled it. */
  fromWallet: number;
  /** The close being taken back, for the screen to describe. */
  event: PerpClose;
}

/** Undo the most recent close on a contract.
 *
 * A wrong figure typed into a close could be corrected nowhere: the event was
 * written, the margin released, the result added up and the money moved, and
 * a contract closed out could not be reopened at all. This puts all of it back,
 * exactly as it was, so the close can be recorded again with the right number.
 *
 * Null when there is nothing to take back. */
export function undoLastClose(perp: Perp): UndoOutcome | null {
  const closes = perp.closes ?? [];
  const event = closes[closes.length - 1];
  if (!event) return null;

  const remaining = closes.slice(0, -1);
  const next: Perp = { ...perp, closes: remaining };
  if (remaining.length === 0) delete next.closes;

  if (event.portion >= 1 - EPSILON) {
    /* A close-out kept the margin and the notional as they were and only marked
       the contract; reopening is taking the marks off again. */
    delete next.closedAt;
    delete next.closedResultUsdt;
  } else {
    next.marginUsdt = perp.marginUsdt + event.releasedUsdt;
    // The share that was left is what the notional was multiplied by.
    const left = 1 - event.portion;
    if (left > EPSILON) next.sizeUsdt = perp.sizeUsdt / left;
    const realised = (perp.realizedUsdt ?? 0) - event.resultUsdt;
    if (Math.abs(realised) < EPSILON && remaining.length === 0) delete next.realizedUsdt;
    else next.realizedUsdt = realised;
  }

  return {
    next,
    fromWallet: event.settled ? 0 : event.releasedUsdt + event.resultUsdt,
    event,
  };
}

/* ---------- funding, and closing on the position's final figure ---------- */

/** What the exchange counts as realised on a contract beyond the closes written
    down here: funding paid or received, and trading fees. Negative when they
    cost money. Null until a read with the key has reported the exchange's
    figure — without it there is nothing to measure against. */
export function fundingAndFees(perp: Perp): number | null {
  if (typeof perp.exchangeRealisedUsdt !== 'number') return null;
  return perp.exchangeRealisedUsdt - (perp.realizedUsdt ?? 0);
}

/** The last close's result, from what the whole position came to.
 *
 * Closing the rest of a position asked for what that rest came to — a figure
 * the exchange does not show. What it shows, once the position is gone, is the
 * whole position's result with funding and fees already in it. Given that, the
 * last close is simply the whole less what the earlier closes already recorded,
 * and the contract's final figure comes out equal to the exchange's. */
export function lastCloseFromTotal(perp: Perp, wholePositionUsdt: number): number {
  return wholePositionUsdt - (perp.realizedUsdt ?? 0);
}
